import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, forkJoin, map, mergeMap, of, switchMap } from 'rxjs';

import { getApiErrorMessage } from '../../../core/utils/api-error.util';
import { VendorService } from '../services/vendor.service';
import { VendorManagementActions } from './vendor-management.actions';

@Injectable()
export class VendorManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly vendorService = inject(VendorService);

  readonly loadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VendorManagementActions.loadList),
      switchMap(({ params }) =>
        forkJoin({
          list: this.vendorService.listVendors(params),
          counts: this.vendorService.getDashboardCounts(),
        }).pipe(
          map(({ list, counts }) =>
            VendorManagementActions.loadListSuccess({
              rows: list.items,
              total: list.total,
              page: list.page,
              pageSize: list.limit,
              counts,
            }),
          ),
          catchError((error: unknown) =>
            of(
              VendorManagementActions.loadListFailure({
                error: getApiErrorMessage(error, 'Unable to load vendors'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadVendor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VendorManagementActions.loadVendor),
      switchMap(({ vendorId }) =>
        this.vendorService.getVendor(vendorId).pipe(
          map((vendor) => VendorManagementActions.loadVendorSuccess({ vendor })),
          catchError((error: unknown) =>
            of(
              VendorManagementActions.loadVendorFailure({
                error: getApiErrorMessage(error, 'Unable to load vendor details'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly loadVendorPartners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VendorManagementActions.loadVendorPartners),
      switchMap(({ vendorId, params }) =>
        this.vendorService.listVendorPartners(vendorId, params).pipe(
          map((response) =>
            VendorManagementActions.loadVendorPartnersSuccess({
              rows: response.items,
              total: response.total,
              page: response.page,
              pageSize: response.limit,
            }),
          ),
          catchError((error: unknown) =>
            of(
              VendorManagementActions.loadVendorPartnersFailure({
                error: getApiErrorMessage(error, 'Unable to load vendor partners'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly createVendor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VendorManagementActions.createVendor),
      switchMap(({ payload }) =>
        this.vendorService.createVendor(payload).pipe(
          map((response) =>
            VendorManagementActions.persistSuccess({
              message: response.message,
              vendor: response.vendor,
            }),
          ),
          catchError((error: unknown) =>
            of(
              VendorManagementActions.persistFailure({
                error: getApiErrorMessage(error, 'Unable to create vendor'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updateVendor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VendorManagementActions.updateVendor),
      switchMap(({ vendorId, payload }) =>
        this.vendorService.updateVendor(vendorId, payload).pipe(
          map((response) =>
            VendorManagementActions.persistSuccess({
              message: response.message,
              vendor: response.vendor,
            }),
          ),
          catchError((error: unknown) =>
            of(
              VendorManagementActions.persistFailure({
                error: getApiErrorMessage(error, 'Unable to update vendor'),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  readonly updateVendorStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(VendorManagementActions.updateVendorStatus),
      switchMap(({ vendorId, status }) =>
        this.vendorService.updateVendorStatus(vendorId, status).pipe(
          map((response) =>
            VendorManagementActions.updateVendorStatusSuccess({
              message: response.message,
              vendor: response.vendor,
            }),
          ),
          catchError((error: unknown) =>
            of(
              VendorManagementActions.updateVendorStatusFailure({
                error: getApiErrorMessage(error, 'Unable to update vendor status'),
              }),
            ),
          ),
        ),
      ),
    ),
  );
}
